import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash/fp';
import { Modal, Radio } from 'antd';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import FormInput from 'components/common/Input';
import { priceFormatter } from 'utils/formatters';
import {
  clearActiveListingsErrorAction,
  updateProductPrice,
  resetToStorePrice,
} from 'store/actions/data';
import UpddatedProductDetailDisplay from 'components/common/ProductDisplay';
import ErrorMessage from 'components/common/ErrorMessage';
import { SET_PRICE, STORE_PRICE } from 'enums/modal-enums';
import { FONTS } from 'styles/theme/fonts';
import { COLORS } from 'styles/theme/colors';

export const EditPrice = ({ modalVisible, product, onCancel }) => {
  const hasError = useSelector((state) => get('activeListings.status.hasError')(state));
  const errorMessage = useSelector((state) => get('activeListings.status.errorMessage')(state));
  const currentPage = useSelector((state) => get('activeListings.metadata.currentPage')(state));
  const priceDollars = priceFormatter(product.priceCents);
  const [selectedRadio, setSelectedRadio] = useState(SET_PRICE);
  const [currentPrice, setCurrentPrice] = useState(priceDollars);
  const dispatch = useDispatch();

  const bodyStyle = {
    height: '600px',
  };

  const clearErrors = () => {
    dispatch(clearActiveListingsErrorAction());
  };

  const resetPrice = () => {
    setCurrentPrice(priceDollars);
    clearErrors();
  };

  const handleEditPrice = (price) => {
    dispatch(
      updateProductPrice(
        {
          product: {
            id: get('storeProductTemplateId')(product),
            priceCents: price,
          },
        },
        currentPage,
      ),
    );
  };

  const handleResetPrice = () => {
    dispatch(
      resetToStorePrice(
        {
          product: {
            id: get('storeProductTemplateId')(product),
          },
        },
        currentPage,
      ),
    );
  };

  const handleClick = () => {
    switch (selectedRadio) {
      case SET_PRICE:
        handleEditPrice((currentPrice * 100) / 1);
        break;
      case STORE_PRICE:
        handleResetPrice();
        break;
      default:
    }
  };

  const handleChange = (e) => {
    clearErrors();
    setCurrentPrice(e.target.value);
  };

  const handleCancel = () => {
    resetPrice();
    onCancel();
  };

  const handleSelectedRadioChange = (e) => {
    setSelectedRadio(e.target.value);
    if (e.target.value === STORE_PRICE) {
      resetPrice();
    }
  };

  const handleBlur = (e) => {
    setCurrentPrice(Number.parseFloat(e.target.value).toFixed(2));
  };

  return (
    <Modal
      visible={modalVisible}
      footer={null}
      title={null}
      wrapClassName="edit-price-modal"
      onCancel={handleCancel}
      bodyStyle={bodyStyle}
    >
      <EditPrice.Divider />
      <EditPrice.Container>
        <EditPrice.ProductAndPriceContainer>
          <EditPrice.ProductSide>
            <UpddatedProductDetailDisplay product={product} />
          </EditPrice.ProductSide>
          <EditPrice.EditPriceSide>
            <EditPrice.Title>Edit Price</EditPrice.Title>
            <Radio.Group onChange={handleSelectedRadioChange} value={selectedRadio}>
              <Radio value={SET_PRICE}>
                <EditPrice.OptionContainer>
                  <EditPrice.Value isSelected={selectedRadio === SET_PRICE}>
                    Set New Price
                  </EditPrice.Value>
                  <EditPrice.RadioOptionDescription isSelected={selectedRadio === SET_PRICE}>
                    Changing the price will immediately be applied to all size variations.
                  </EditPrice.RadioOptionDescription>
                  {selectedRadio === SET_PRICE && (
                    <FormInput
                      defaultValue={priceDollars}
                      value={currentPrice}
                      hasError={hasError}
                      onChange={handleChange}
                      onFocus={clearErrors}
                      isMoney
                      placeholder="New Price"
                      onBlur={handleBlur}
                    />
                  )}
                  {hasError && errorMessage && <ErrorMessage message={errorMessage} />}
                </EditPrice.OptionContainer>
              </Radio>
              <Radio value={STORE_PRICE}>
                <EditPrice.OptionContainer>
                  <EditPrice.Value isSelected={selectedRadio === STORE_PRICE}>
                    Reset to Store Price
                  </EditPrice.Value>
                  <EditPrice.RadioOptionDescription isSelected={selectedRadio === STORE_PRICE}>
                    Prices will reset to store price at 3:00AM PST the following business day.
                  </EditPrice.RadioOptionDescription>
                </EditPrice.OptionContainer>
              </Radio>
            </Radio.Group>
          </EditPrice.EditPriceSide>
        </EditPrice.ProductAndPriceContainer>
        <EditPrice.BackButton onClick={handleCancel}>CANCEL</EditPrice.BackButton>
        <EditPrice.EditPriceButton onClick={handleClick}>DONE</EditPrice.EditPriceButton>
      </EditPrice.Container>
    </Modal>
  );
};

EditPrice.Container = styled.div`
  color: ${COLORS.primaryEditPriceModalText};
  width: 1000px;
`;

EditPrice.Title = styled.div`
  font-size: 20px;
  line-height: 24px;
  font-family: ${FONTS.default};
  margin: 7px 24px 40px;
  display: flex;
  justify-content: center;
  text-transform: capitalize;
`;

EditPrice.ProductAndPriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin-top: 64px;
  margin-bottom: 112px;
`;

EditPrice.ProductSide = styled.div`
  width: 50%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`;

EditPrice.EditPriceSide = styled.div`
  width: 50%;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  padding-left: 55px;
`;

EditPrice.BackButton = styled.button`
  color: ${COLORS.primaryEditPriceModalText};
  font-weight: 500;
  font-family: ${FONTS.emphasized};
  background-color: ${COLORS.primaryBackground};
  width: 50%;
  text-align: center;
  position: absolute;
  bottom: 0;
  border: 1px solid ${COLORS.primaryEditPriceBorder};
  left: 0;
  height: 80px;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${COLORS.secondaryEditPriceButtonHoverBackground};
  }

  &:disabled {
    color: ${COLORS.primaryEditPriceDisabledButtonText};
    border: 1px solid ${COLORS.secondaryEditPriceBorder};
    cursor: not-allowed;
    background-color: ${COLORS.primaryBackground};
  }
`;

EditPrice.EditPriceButton = styled.button`
  color: ${COLORS.primaryEditPriceModalText};
  background-color: ${COLORS.primaryEditPriceButtonBackground};
  font-weight: 500;
  width: 50%;
  text-align: center;
  position: absolute;
  bottom: 0;
  border: none;
  right: 0;
  height: 80px;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${COLORS.light2};
  }

  &:disabled {
    background-color: ${COLORS.primaryEditPriceDisabledButtonBackground};
    color: ${COLORS.primaryEditPriceDisabledButtonText};
    cursor: not-allowed;
    border: 1px solid ${COLORS.primaryEditPriceBorder};
  }
`;

EditPrice.PriceInfo = styled.div`
  width: 277px;
  height: 85px;
  border: solid 1px ${COLORS.primaryEditPriceInfoText};
  margin-top: 38px;
  display: flex;
  justify-content: space-evenly;
`;

EditPrice.Value = styled.div`
  text-transform: capitalize;
  color: ${(props) =>
    props.isSelected ? COLORS.primaryEditPriceModalText : COLORS.secondaryEditPriceModalText};
  font-size: 16px;
  line-height: 19px;
  font-family: ${FONTS.emphasized};
  letter-spacing: 0.005em;
  margin-top: 2px;
  margin-bottom: 8px;
`;

EditPrice.OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 51px;
`;

EditPrice.RadioOptionDescription = styled.div`
  color: ${(props) =>
    props.isSelected ? COLORS.secondaryText : COLORS.secondaryEditPriceModalText};
  font-size: 16px;
  font-family: ${FONTS.default};
  line-height: 22px;
  max-width: 365px;
  white-space: pre-wrap;
  margin-bottom: 16px;
`;

EditPrice.Divider = styled.div`
  background: ${COLORS.primaryEditPriceModalBackground};
  width: 1px;
  height: 100%;
  position: absolute;
  top: 0;
  left: 522px;
`;

EditPrice.propTypes = {
  onCancel: PropTypes.func,
  modalVisible: PropTypes.bool,
  product: PropTypes.shape({
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default EditPrice;
